import { useState } from 'react';
import { Layers, Upload, Check } from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import { cn } from '@/lib/utils';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import {
  workflowApi,
  type AgentFormatId,
  type GeneratedAgentFile,
} from '@/services/workflowApi';
import CollapsibleSection from './CollapsibleSection';

interface FormatChoice {
  id: AgentFormatId;
  label: string;
  path: string;
  consumers: string;
}

// Mirrors the backend renderer registry (common/agent_formats.py). The backend
// validates the selection, so an id that drifts out of sync fails loudly there.
const FORMATS: FormatChoice[] = [
  {
    id: 'claude',
    label: 'CLAUDE.md',
    path: '.claude/CLAUDE.md',
    consumers: 'Claude Code',
  },
  {
    id: 'agents',
    label: 'AGENTS.md',
    path: 'AGENTS.md',
    consumers: 'Codex, Cursor, Copilot, Gemini CLI, Devin, Windsurf, Zed, Aider, Amazon Q',
  },
  {
    id: 'skill',
    label: 'Agent Skills',
    path: '.agents/skills/*/SKILL.md',
    consumers: 'Claude, Cursor, Copilot, Kiro and ~40 other clients',
  },
  {
    id: 'cursor',
    label: 'Cursor rules',
    path: '.cursor/rules/*.mdc',
    consumers: 'Cursor',
  },
];

export default function AgentConfigSection() {
  const { selectedRepo } = useWorkspace();
  const [selected, setSelected] = useState<AgentFormatId[]>(['claude', 'agents']);
  const [files, setFiles] = useState<GeneratedAgentFile[]>([]);
  const [activePath, setActivePath] = useState('');
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishedPaths, setPublishedPaths] = useState<string[]>([]);
  const [error, setError] = useState('');

  function toggle(id: AgentFormatId) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id],
    );
  }

  async function handleGenerate() {
    if (!selectedRepo || selected.length === 0) return;
    setGenerating(true);
    setError('');
    setPublishedPaths([]);
    try {
      const result = await workflowApi.generateAgentConfig(
        selectedRepo,
        selectedRepo.split('/')[1],
        selected,
      );
      setFiles(result.files);
      setActivePath(result.files[0]?.path ?? '');
    } catch {
      setError('Could not generate agent config. Check the repo connection and try again.');
    } finally {
      setGenerating(false);
    }
  }

  function updateActive(content: string) {
    setFiles((prev) =>
      prev.map((f) => (f.path === activePath ? { ...f, content } : f)),
    );
  }

  async function handlePublish() {
    if (!selectedRepo || files.length === 0) return;
    setPublishing(true);
    setError('');
    const committed: string[] = [];
    try {
      // Every format commits through the same publish-file path.
      for (const file of files) {
        await workflowApi.publishFile(
          selectedRepo,
          file.path,
          file.content,
          `chore: update ${file.path} (via SystemDox)`,
        );
        committed.push(file.path);
      }
    } catch {
      setError(`Published ${committed.length} of ${files.length} files, then failed.`);
    } finally {
      setPublishedPaths(committed);
      setPublishing(false);
    }
  }

  const active = files.find((f) => f.path === activePath);

  return (
    <CollapsibleSection
      title="Agent configuration"
      description="Author your standards once — emit config for every agent your team uses"
      filePath={`${FORMATS.length} formats`}
      icon={Layers}
      exists={files.length > 0}
      defaultOpen
    >
      <div className="space-y-4">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Rendered from your tech stack, project rules, guardrails and prompt templates.
          Editing a file below changes only what gets committed — the standards stay the
          source of truth.
        </p>

        {/* Format multi-select */}
        <div className="grid gap-2 sm:grid-cols-2">
          {FORMATS.map((format) => {
            const checked = selected.includes(format.id);
            return (
              <button
                key={format.id}
                type="button"
                role="checkbox"
                aria-checked={checked}
                onClick={() => toggle(format.id)}
                className={cn(
                  'flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors',
                  checked
                    ? 'border-purple-400 bg-purple-50 dark:border-purple-600 dark:bg-purple-900/20'
                    : 'border-slate-200 hover:border-slate-300 dark:border-slate-800 dark:hover:border-slate-700',
                )}
              >
                <span
                  className={cn(
                    'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    checked
                      ? 'border-purple-600 bg-purple-600 text-white'
                      : 'border-slate-300 dark:border-slate-600',
                  )}
                >
                  {checked && <Check className="h-3 w-3" />}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">
                    {format.label}
                  </span>
                  <span className="block font-mono text-[11px] text-slate-400">{format.path}</span>
                  <span className="mt-0.5 block text-[11px] leading-snug text-slate-500 dark:text-slate-400">
                    {format.consumers}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleGenerate}
            disabled={generating || !selectedRepo || selected.length === 0}
            className="flex items-center gap-2 rounded-md bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
          >
            {generating ? (
              <Spinner size="sm" tone="current" />
            ) : (
              <Layers className="h-3.5 w-3.5" />
            )}
            {generating ? 'Generating...' : 'Generate'}
          </button>
          {!selectedRepo && (
            <span className="text-xs text-slate-500">Select a repository first.</span>
          )}
        </div>

        {error && (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600 dark:border-red-900 dark:bg-red-900/20 dark:text-red-400">
            {error}
          </p>
        )}

        {files.length > 0 && (
          <div className="space-y-3">
            {/* One tab per emitted file */}
            <div className="flex flex-wrap gap-1.5">
              {files.map((file) => (
                <button
                  key={file.path}
                  onClick={() => setActivePath(file.path)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md px-2.5 py-1 font-mono text-[11px] transition-colors',
                    file.path === activePath
                      ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700',
                  )}
                >
                  {publishedPaths.includes(file.path) && (
                    <Check className="h-3 w-3 text-emerald-500" />
                  )}
                  {file.path}
                </button>
              ))}
            </div>

            {active && (
              <textarea
                value={active.content}
                onChange={(e) => updateActive(e.target.value)}
                spellCheck={false}
                className="h-64 w-full resize-y rounded-lg border border-slate-200 bg-white p-3 font-mono text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
            )}

            <button
              onClick={handlePublish}
              disabled={publishing}
              className="flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {publishing ? (
                <Spinner size="sm" tone="current" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
              {publishing
                ? 'Publishing...'
                : publishedPaths.length === files.length
                  ? `Published ${files.length} file${files.length === 1 ? '' : 's'}`
                  : `Publish ${files.length} file${files.length === 1 ? '' : 's'} to ${selectedRepo}`}
            </button>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}
